import { ElementType, KeyboardEvent, useEffect, useRef, useState } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";

import {
  PreviewFieldVariant,
  StyledDisplayText,
  StyledEditableField,
  StyledEditableLabel,
  StyledEditAffordance,
  StyledEditorInput,
  StyledFieldError,
  StyledFieldWrapper,
} from "./styled";
import { TokenText } from "./TokenText";

interface IEditableFieldProps {
  name: string;
  label: string;
  variant: PreviewFieldVariant;
  rules?: RegisterOptions;
  /** Container to render instead of the default dashed box (e.g. a button). */
  container?: ElementType;
  /** Inverts the hover badge for containers with a dark background. */
  onDark?: boolean;
}

export function EditableField({
  name,
  label,
  variant,
  rules,
  container,
  onDark,
}: IEditableFieldProps) {
  const { control, trigger } = useFormContext();

  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      const { length } = inputRef.current.value;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const Container = container || StyledEditableField;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, onBlur, value }, fieldState }) => {
        // Read from fieldState, not formState.errors: this render prop
        // re-renders on validation, the enclosing component does not.
        const hasError = !!fieldState.error;

        const handleBlur = () => {
          setIsEditing(false);
          onBlur();
          trigger(name);
        };

        const autoResize = (element: HTMLTextAreaElement | null) => {
          if (element) {
            element.style.height = "auto";
            element.style.height = `${element.scrollHeight}px`;
          }
        };

        return (
          <StyledFieldWrapper>
            <Container
              $invalid={hasError}
              role="button"
              tabIndex={0}
              data-test-id={`${name}-editable`}
              aria-label={`Edit ${label.toLowerCase()}`}
              onClick={() => setIsEditing(true)}
              onKeyDown={(event: KeyboardEvent) => {
                // Only act on keys aimed at the container itself. Without this
                // the handler also fires on bubble from the textarea and
                // swallows every space and newline the user types.
                if (isEditing || event.target !== event.currentTarget) {
                  return;
                }

                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsEditing(true);
                }
              }}
            >
              <StyledEditableLabel>
                <i className="pi pi-pencil" />
                {label}
              </StyledEditableLabel>

              <StyledEditAffordance $onDark={onDark} aria-hidden="true">
                <i className="pi pi-pencil" />
              </StyledEditAffordance>

              {isEditing ? (
                <StyledEditorInput
                  ref={(element: HTMLTextAreaElement | null) => {
                    inputRef.current = element;
                    autoResize(element);
                  }}
                  $variant={variant}
                  id={name}
                  rows={1}
                  value={value || ""}
                  aria-label={label}
                  onChange={(event) => {
                    autoResize(event.target);
                    onChange(event);
                  }}
                  onBlur={handleBlur}
                />
              ) : (
                // Deliberately no fallback text: an empty field must read as
                // empty so the required-field error makes sense, rather than
                // showing the patient page's default as if it were saved.
                <StyledDisplayText $variant={variant}>
                  <TokenText value={value} />
                </StyledDisplayText>
              )}
            </Container>

            {hasError && (
              <StyledFieldError>
                <small className="p-error">{fieldState.error?.message}</small>
              </StyledFieldError>
            )}
          </StyledFieldWrapper>
        );
      }}
    />
  );
}
